import CircularFilledIcon from "@/components/CircularButton/styled/CircularFilledIcon";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import KnowtMUIThemeProvider from "@/components/KnowtMUIThemeProvider";
import Select from "@/components/Select";
import WithOptionsMenu from "@/components/WithOptionsMenu";
import CheckBox from "@/components/styled/input/CheckBox";
import { dynamicKeyValueStore } from "@/utils/dynamicKeyValueStore";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import noop from "@knowt/syncing/utils/noop";
import Table, { TableProps } from "@mui/material/Table";
import TableBody, { TableBodyProps } from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableContainer, { TableContainerProps } from "@mui/material/TableContainer";
import TableHead, { TableHeadProps } from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { ArrowDownNarrowWideIcon, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDownIcon } from "lucide-react";
import React, { ReactNode, useState, Children, isValidElement, Fragment, ReactElement } from "react";
import { SortOrder, SortSettings } from "./types";

type TableColumnProps<T> = {
    title: string;
    sortKey?: string | number;
    cellProps?: TableCellProps;
    children?: (cell: T) => ReactNode;
    sortOptions?: { label: string; value: () => void; isSelected?: boolean }[];
};

type StyledTableProps<T> = {
    optionsSection?: ReactNode;
    paginateBy?: number;
    disableSelection?: boolean;
    tableContainerProps?: TableContainerProps;
    containerStyle?: React.CSSProperties;
    tableProps?: TableProps;
    headerProps?: TableHeadProps;
    bodyProps?: TableBodyProps;
    cellProps?: TableCellProps;
    rows?: T[] | null;
    sortSettings?: SortSettings | null;
    autoSort?: boolean;
    selectedRows?: T[];
    onRowSelect?: (selectedRows: T[]) => void;
    onSortChange?: (sortSettings: SortSettings) => void;
    onRowClick?: (row: T) => void;
    emptyMessage?: string;
    hideBottomPagination?: boolean;
    hideOptionSection?: boolean;
    isRowSelected?: (row: T) => boolean;
    children?: ReactNode;
    customEmptyState?: ReactNode;
};

export const StyledTableColumn = <_T,>(_: TableColumnProps<_T>) => null;

const isFragment = (element: React.ReactElement) => {
    return element.type === Fragment;
};

type TableColumnPropsWithRender<T> = TableColumnProps<T> & {
    render: ReactNode | undefined | ((cell: T) => ReactNode | undefined);
};

const StyledTable = <T,>({
    optionsSection,
    paginateBy = 10,
    disableSelection,
    tableContainerProps,
    containerStyle,
    tableProps,
    headerProps,
    bodyProps,
    cellProps,
    children,
    sortSettings,
    rows,
    selectedRows,
    emptyMessage,
    customEmptyState,
    hideBottomPagination,
    hideOptionSection,
    isRowSelected,
    onRowSelect,
    onSortChange,
    onRowClick,
}: StyledTableProps<T>) => {
    const columns = Children.toArray(children).reduce(
        (acc, child: React.ReactElement<TableColumnProps<T>>) => {
            if (child.type === StyledTableColumn) {
                acc.push({
                    ...child.props,
                    render: child.props.children,
                });
                return acc;
            }

            let nestedChildOfCurrentChild = child;
            let nestedChildrenFound = false;
            const nestedChildren = [] as TableColumnPropsWithRender<T>[];

            while (!nestedChildrenFound) {
                if (nestedChildOfCurrentChild.type === StyledTableColumn) {
                    nestedChildren.push(nestedChildOfCurrentChild);
                    nestedChildrenFound = true;
                } else if (
                    nestedChildOfCurrentChild.props?.children &&
                    Children.toArray(nestedChildOfCurrentChild.props.children).filter(Boolean).length > 0 &&
                    Children.toArray(nestedChildOfCurrentChild.props.children)
                        .filter(Boolean)
                        .every(child => isValidElement(child) && child.type === StyledTableColumn)
                ) {
                    nestedChildren.push(...Children.toArray(nestedChildOfCurrentChild.props.children));
                    nestedChildrenFound = true;
                } else {
                    if (isFragment(nestedChildOfCurrentChild)) {
                        nestedChildrenFound = true;
                        break;
                    }

                    const nextChild = nestedChildOfCurrentChild.type?.(nestedChildOfCurrentChild.props);

                    if (!nextChild || !isValidElement(nextChild)) {
                        nestedChildrenFound = true;
                        break;
                    }

                    nestedChildOfCurrentChild = nextChild as ReactElement<TableColumnProps<T>>;
                }
            }

            if (!nestedChildren) return acc;

            return acc.concat(
                nestedChildren.map(child => ({
                    ...child.props,
                    render: child.props.children,
                }))
            );
        },
        [] as (TableColumnProps<T> & { render: ReactNode | undefined | ((cell: T) => ReactNode | undefined) })[]
    );

    const tableId =
        "styled-table-" +
        columns
            .map(column => column.title)
            .join("-")
            .split(" ")
            .join("");

    const [page, setPage] = useState(0);

    const [rowsPerPage, setRowsPerPage] = useState(
        Number.parseInt(dynamicKeyValueStore.get(tableId + "-rowsPerPage")) || paginateBy
    );

    const totalPages = Math.ceil((rows?.length || 0) / rowsPerPage);
    const currentPageRows = rows?.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    const renderTableHeader = () => (
        <TableHead {...headerProps} style={{ backgroundColor: themeColors.neutralWhite, ...headerProps?.style }}>
            <TableRow>
                {!disableSelection && (
                    <TableCell
                        key={"selection"}
                        {...cellProps}
                        width={5}
                        style={{
                            paddingRight: "1.6rem",
                            backgroundColor: themeColors.neutralWhite,
                            ...cellProps?.style,
                        }}>
                        <CheckBox
                            isChecked={selectedRows?.length === rows?.length}
                            onCheck={
                                rows?.length
                                    ? () => onRowSelect?.((selectedRows?.length || 0) === rows?.length ? [] : rows)
                                    : noop
                            }
                        />
                    </TableCell>
                )}
                {columns.map(column => (
                    <TableCell
                        key={column.title}
                        {...column.cellProps}
                        style={{
                            fontWeight: "600",
                            ...cellProps?.style,
                            ...column?.cellProps?.style,
                        }}>
                        <WithOptionsMenu
                            menuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: "2rem",
                                        width: "28rem",
                                        marginTop: "1rem",
                                        marginLeft: "8rem",
                                        padding: "2rem",
                                    },
                                    className: "scrollbar",
                                },
                                MenuListProps: {
                                    sx: {
                                        padding: 0,
                                        "& .MuiMenuItem-root": {
                                            padding: 0,
                                            marginBottom: spacing.XS,
                                            borderRadius: "0.5rem",
                                            transition: "background-color 0.3s",
                                        },
                                    },
                                },

                                disablePortal: true,
                                BackdropProps: { style: { zIndex: 0 } },
                            }}
                            options={
                                !!column.sortKey && !!column.sortOptions?.length
                                    ? [
                                          {
                                              label: "",
                                              onClick: noop,
                                              node: (
                                                  <span
                                                      className="secondaryTextBold2"
                                                      style={{
                                                          color: themeColors.neutral2,
                                                          marginBottom: "1.5rem",
                                                      }}
                                                      data-testid="auto-id-136ed509-923e-420e-9dd0-fb1035ecbc83">
                                                      Sorting Options
                                                  </span>
                                              ),
                                          },
                                      ].concat(
                                          column.sortOptions.map(({ label, value, isSelected }) => ({
                                              label: label,
                                              onClick: value,
                                              node: (
                                                  <FlexRowAlignCenter
                                                      style={{
                                                          gap: spacing.XS,
                                                          color: themeColors.neutralBlack,
                                                          padding: "0.6rem",
                                                          backgroundColor: isSelected
                                                              ? themeColors.background
                                                              : "inherit",
                                                          borderRadius: "0.5rem",
                                                      }}>
                                                      <CircularFilledIcon
                                                          borderRadius="0.5rem"
                                                          IconProps={{
                                                              size: iconSizes.SM_S,
                                                              strokeWidth: 1.5,
                                                          }}
                                                          Icon={ArrowDownNarrowWideIcon}
                                                          color={themeColors.neutralBlack}
                                                          secondaryColor={themeColors.neutralBlack}
                                                          primaryBg={themeColors.background}
                                                          secondaryBg={themeColors.background}
                                                          sx={{
                                                              padding: "0.2rem",
                                                              "&:hover": {
                                                                  boxShadow: "none",
                                                              },
                                                          }}
                                                          radius="2.5rem"
                                                      />
                                                      <span
                                                          className="secondaryText2"
                                                          data-testid="auto-id-5e675423-f891-4ac3-8e18-25922f65337f">{label}</span>
                                                  </FlexRowAlignCenter>
                                              ),
                                          }))
                                      )
                                    : []
                            }>
                            {({ openMenu }) => (
                                <FlexRowAlignCenter
                                    onClick={e =>
                                        column.sortKey
                                            ? column.sortOptions?.length
                                                ? openMenu(e)
                                                : onSortChange?.({
                                                      sortBy: column.sortKey,
                                                      order:
                                                          sortSettings?.sortBy === column.sortKey
                                                              ? sortSettings?.order === SortOrder.ASC
                                                                  ? SortOrder.DESC
                                                                  : SortOrder.ASC
                                                              : SortOrder.DESC,
                                                  })
                                            : noop
                                    }
                                    style={{
                                        userSelect: "none",
                                        cursor: column.sortKey ? "pointer" : "default",
                                        gap: "0.8rem",
                                    }}>
                                    {column.title}
                                    {(!!column.sortKey || column.sortKey === 0) &&
                                        (column.sortOptions?.length || sortSettings?.sortBy !== column.sortKey ? (
                                            <ChevronsUpDownIcon
                                                size={iconSizes.SM}
                                                style={{
                                                    cursor: "pointer",
                                                }}
                                            />
                                        ) : (
                                            <ChevronDown
                                                size={iconSizes.SM}
                                                style={{
                                                    cursor: "pointer",
                                                    transform: `rotate(${
                                                        sortSettings?.order === SortOrder.DESC ? "0" : "180deg"
                                                    })`,
                                                    transition: "transform 0.2s",
                                                }}
                                            />
                                        ))}
                                </FlexRowAlignCenter>
                            )}
                        </WithOptionsMenu>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );

    const renderTableBody = () => (
        <TableBody {...bodyProps}>
            {currentPageRows?.map(row => (
                <TableRow
                    key={tableId + JSON.stringify(row)}
                    onClick={() => onRowClick?.(row)}
                    sx={{
                        "&:hover": {
                            backgroundColor: onRowClick ? themeColors.background : "inherit",
                            transition: "background-color 0.2s",
                        },
                    }}
                    style={{
                        cursor: onRowClick ? "pointer" : "default",
                    }}>
                    {!disableSelection && (
                        <TableCell
                            key={"selection" + JSON.stringify(row)}
                            {...cellProps}
                            width={5}
                            style={{
                                paddingRight: "1.6rem",
                                ...cellProps?.style,
                            }}>
                            <CheckBox
                                isChecked={isRowSelected?.(row) || !!selectedRows?.includes(row)}
                                onCheck={() =>
                                    onRowSelect?.(
                                        selectedRows?.includes(row)
                                            ? selectedRows?.filter(selectedRow => selectedRow !== row)
                                            : [...(selectedRows || []), row]
                                    )
                                }
                            />
                        </TableCell>
                    )}
                    {columns.map((column, index) => (
                        <TableCell
                            key={`${tableId + JSON.stringify(row)}-${index}`}
                            {...cellProps}
                            {...column.cellProps}>
                            {column.render?.(row)}
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );

    return (
        <FlexColumn
            style={{
                backgroundColor: themeColors.neutralWhite,
                borderRadius: "2rem",
                overflow: "hidden",
                width: "100%",
                ...(!rows?.length ? { height: "100%", flex: 1 } : {}),
                ...containerStyle,
            }}>
            {!hideOptionSection && (
                <FlexRowAlignCenter
                    style={{
                        justifyContent: "space-between",
                        padding: "2.4rem 2.4rem 1.6rem 1.6rem",
                        width: "100%",
                        gap: "1.6rem",
                        minHeight: "6rem",
                        backgroundColor: themeColors.neutralWhite,
                    }}>
                    {optionsSection}
                </FlexRowAlignCenter>
            )}
            <KnowtMUIThemeProvider>
                <TableContainer
                    {...tableContainerProps}
                    sx={{
                        ...tableContainerProps?.sx,
                        ".MuiTableCell-root": {
                            padding: "1.6rem",
                            height: "7.8rem",
                            color: themeColors.neutralBlack,
                            ...tableContainerProps?.sx?.[".MuiTableCell-root"],
                        },
                    }}>
                    <Table
                        {...tableProps}
                        sx={{ borderColor: themeColors.background, ...tableProps?.sx }}
                        data-testid="auto-id-d0584979-3582-4662-ae21-3234cb556b85">
                        {renderTableHeader()}
                        {renderTableBody()}
                    </Table>
                </TableContainer>
                {emptyMessage && !customEmptyState && !rows?.length && (
                    <FlexRowAlignCenter
                        style={{
                            padding: "2rem 3.5rem",
                            width: "100%",
                            justifyContent: "center",
                            gap: "1.6rem",
                            userSelect: "none",
                            minHeight: "50vh",
                        }}>
                        <p
                            className="bodyBold1"
                            data-testid="auto-id-3766fe60-c71a-44af-bc7a-3c89e4b79e1a">{emptyMessage}</p>
                    </FlexRowAlignCenter>
                )}
                {customEmptyState && !rows?.length && customEmptyState}
                {!hideBottomPagination && (
                    <FlexRowAlignCenter
                        style={{
                            padding: "2rem  3.5rem",
                            width: "100%",
                            justifyContent: "flex-end",
                            gap: "1.6rem",
                            userSelect: "none",
                            minHeight: "6rem",
                        }}>
                        {(rows?.length || 0) > 5 && (
                            <FlexRowAlignCenter
                                style={{
                                    gap: "1.6rem",
                                }}>
                                <p
                                    className="bodyBold2"
                                    data-testid="auto-id-7d632a5a-a96a-4afe-853d-65d933724bf5">Rows per page:</p>
                                <Select
                                    options={[5, 10, 25, 50, 100].map(value => ({
                                        label: value.toString(),
                                        value,
                                    }))}
                                    onChange={({ value }) => {
                                        dynamicKeyValueStore.set(tableId + "-rowsPerPage", value);
                                        setRowsPerPage(value);
                                    }}
                                    menuSx={{ width: "25rem", padding: "1rem" }}
                                    btnSx={{
                                        gap: "1rem",
                                        height: "4.8rem",
                                        backgroundColor: themeColors.neutralWhite,
                                        padding: "1.2rem 2.4rem",
                                        border: `2px solid ${themeColors.neutral1}`,
                                        borderRadius: "2rem",
                                        width: "fit-content",
                                        justifyContent: "space-between",
                                    }}
                                    selected={
                                        paginateBy
                                            ? { label: rowsPerPage.toString(), value: rowsPerPage }
                                            : { label: "5", value: 5 }
                                    }
                                    transitionOrigin={{
                                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                        transformOrigin: { vertical: "top", horizontal: "left" },
                                    }}
                                    adornment={<ChevronDown size={iconSizes.SM} />}
                                    data-testid="auto-id-91968c28-c595-4523-869e-c7b0cd08badf" />
                                <p
                                    className="bodyBold2"
                                    data-testid="auto-id-edade147-4c52-4e19-a6aa-1026482b5d59">{`${page * rowsPerPage + 1}-${Math.min(
                                    rows?.length || 0,
                                    rowsPerPage * (page + 1)
                                )} of ${rows?.length}`}</p>
                                <ChevronLeft
                                    size={iconSizes.SM}
                                    style={{ cursor: "pointer", opacity: page === 0 ? 0.5 : 1 }}
                                    onClick={page === 0 ? undefined : () => setPage(page - 1)}
                                />
                                <ChevronRight
                                    size={iconSizes.SM}
                                    style={{
                                        cursor: "pointer",
                                        opacity: page === totalPages - 1 ? 0.5 : 1,
                                    }}
                                    onClick={page === totalPages - 1 ? undefined : () => setPage(page + 1)}
                                />
                            </FlexRowAlignCenter>
                        )}
                    </FlexRowAlignCenter>
                )}
            </KnowtMUIThemeProvider>
        </FlexColumn>
    );
};

export default StyledTable;
